import { cn } from '@/lib/utils'

import type { ProductCardSkeletonProps } from '../../types'

/**
 * CompactProductCardSkeleton - Loading placeholder matching CompactProductCard.
 *
 * Compact card: row 1 = fixed-height image | name lines, bottom = reward line + two
 * price rows (PRP / strikethrough, final price) + add to cart.
 * Bottom block mirrors the real card's spacing (pt-1 pb-1.5, no mt-auto) and reserves
 * a reward-points row so the placeholder height ≈ the typical loaded card (less CLS).
 */
export function CompactProductCardSkeleton({ className }: ProductCardSkeletonProps) {
  return (
    <div
      className={cn(
        'flex flex-col bg-white w-full overflow-hidden border-2 border-white animate-pulse',
        className,
      )}
    >
      {/* Row 1: image | name */}
      <div className="grid grid-cols-2 gap-2 p-2">
        <div className="h-[100px] xl:h-[120px] bg-gray-200 rounded" />
        <div className="flex flex-col gap-1.5">
          <div className="h-3 bg-gray-200 rounded w-full" />
          <div className="h-3 bg-gray-200 rounded w-5/6" />
          <div className="h-3 bg-gray-200 rounded w-2/3" />
        </div>
      </div>

      {/* Bottom: reward line + price + add to cart (matches the real card's pt-1 pb-1.5, no mt-auto) */}
      <div className="w-full border-t border-cards-bg px-2 pt-1 pb-1.5">
        <div className="h-3 bg-gray-200 rounded w-16 mb-1" />
        {/* Same layout as the real card: stacked below md, price (75%) | button (25%) from md up */}
        <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:gap-1">
          <div className="md:w-3/4">
            <div className="h-3 bg-gray-200 rounded w-20 mb-1" />
            <div className="h-5 xl:h-7 bg-gray-200 rounded w-24" />
          </div>
          <div className="h-[30px] md:h-[35px] xl:h-[45px] md:w-1/4 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  )
}
